import { gql } from '@apollo/client'
import employeeWithBuFragment from '../employee/fragments/employeeWithBu.fragment'
import employeeSkillStatusFragment from '../skill-status/employeeSkillStatus.fragment'

export const COMPANY_SETTINGS_REVIEWER = gql`
  query companySettingsReviewer($companyId: HashedId!) {
    reviewersByCompanyId(companyId: $companyId) {
      id
      reviewer {
        id
        firstName
        lastName
        companyName
        buName
        currentLevel
        avatarUrl
      }
      employee {
        id
        firstName
        lastName
        companyName
        buName
        currentLevel
        avatarUrl
      }
      fromDate
      type
      canDelete
    }
    checkCompanyManagerOfCompany(companyId: $companyId) {
      permit
    }
    activeEmployeesOfCompany(companyId: $companyId) {
      ...EmployeeWithBu
    }
    activeEmployeesOfCompanyWithoutPartners(companyId: $companyId) {
      ...EmployeeWithBu
    }
  }
  ${employeeWithBuFragment}
`

export const BU_COMPANY_SETTINGS_REVIEWER = gql`
  query buCompanySettingsReviewer($buId: HashedId!) {
    reviewersRelationOfBusinessUnitId(buId: $buId) {
      id
      reviewer {
        id
        firstName
        lastName
        companyName
        buName
        currentLevel
        avatarUrl
      }
      employee {
        id
        firstName
        lastName
        companyName
        buName
        currentLevel
        avatarUrl
      }
      fromDate
      type
      canDelete
    }
    activeSubordinatesOfBuManager(buId: $buId) {
      ...EmployeeWithBu
    }
  }
  ${employeeWithBuFragment}
`

export const COMPANY_SETTINGS_TARGETS = gql`
  query CompanySettingsTargets($companyId: HashedId!, $country: Country!) {
    targetTemplatesOfCompany(companyId: $companyId, country: $country) {
      id
      objectiveTitle
      lastModifier {
        id
        firstName
        lastName
        avatarUrl
      }
      lastEditedDate
      creator {
        id
        firstName
        lastName
        avatarUrl
      }
      objectiveKeyResults {
        id
        text
        displayOrder
        globalId
      }
      objectiveNotes {
        id
        text
        date
        author {
          id
          firstName
          lastName
          avatarUrl
        }
      }
    }
  }
`

export const COMPANY_SETTINGS_EMPLOYEES = gql`
  query CompanySettingsEmployees($companyId: HashedId!, $employeeLevelCountry: Country!) {
    subordinateOfCompany(companyId: $companyId) {
      id
      firstName
      lastName
      currentLevel
      avatarUrl
      buName
      currencyForPayments
      replyEntryDate
      contractValidTo
      country
      salary
      targetBonus
      resultScore
      careerPathEvaluation {
        salaryEvaluation
        bonusEvaluation
        summary
        salary
        targetBonus
      }
      careerPath {
        careerPathType
        country
        level
        min
        max
      }
      timezone
      skillStatus {
        ...SkillStatusFragment
      }
    }
    resignedEmployeesOfCompany(companyId: $companyId) {
      employeesWithWrongStatusOrMissingContractDTO {
        fullName
        businessUnitId
      }
      resignedEmployees {
        id
        firstName
        lastName
        level
        businessUnit {
          id
          name
          displayName
          company {
            id
            name
          }
        }
        salary
        currency
        bonus
        startDate
        endDate
        reason
        toBePaid {
          amount
          currency
        }
        timezone
      }
    }
    employeeLevels: getCountryEmployeeLevels(country: $employeeLevelCountry)
  }
  ${employeeSkillStatusFragment}
`

export const SUBORDINATE_OF_COMPANY = gql`
  query SubordinateOfCompany($companyId: HashedId!) {
    subordinateOfCompany(companyId: $companyId) {
      id
      firstName
      lastName
      currentLevel
      avatarUrl
      buName
      currencyForPayments
      replyEntryDate
      contractValidTo
      country
      salary
      targetBonus
      resultScore
      careerPathEvaluation {
        salaryEvaluation
        bonusEvaluation
        summary
        salary
        targetBonus
      }
      careerPath {
        careerPathType
        country
        level
        min
        max
      }
      timezone
      skillStatus {
        ...SkillStatusFragment
      }
    }
  }
  ${employeeSkillStatusFragment}
`

export const COMPANY_SETTINGS_RANKING = gql`
  query CompanySettingsRanking($companyId: HashedId!, $country: Country!) {
    employeesAndTags(companyId: $companyId, country: $country) {
      id
      firstName
      lastName
      currentLevel
      avatarUrl
      buName
      companyName
      tags {
        id
        type
        addedDate
        removedDate
        note
        temporary
      }
    }
    companyRankingSettingsForCountry(companyId: $companyId, country: $country) {
      topPercentage
      lowPercentage
      lowPercentageIsBlocker
      rankingIsDisabled
      tagAssignmentIsInProgress
    }
  }
`

export const COMPANY_SETTINGS_PYRAMIDS = gql`
  query companySettingsPyramids($companyId: HashedId!, $country: Country!) {
    ageReferencePyramid: referencedPyramidsByCompanyIdAndCountry(companyId: $companyId, country: $country, pyramidType: Age) {
      id
      companyId
      referencePyramidType
      referencePyramidDetails {
        id
        displayOrder
        range
        limitType
        percentage
      }
    }
    levelReferencePyramid: referencedPyramidsByCompanyIdAndCountry(companyId: $companyId, country: $country, pyramidType: Level) {
      id
      companyId
      referencePyramidType
      referencePyramidDetails {
        id
        displayOrder
        range
        limitType
        percentage
      }
    }
    ageReferencePyramidTemplate: referencedPyramidsTemplateByCountryAndType(country: $country, referencePyramidType: Age) {
      id
      title
      country
      referencePyramidType
      referencePyramidTemplateDetails {
        id
        range
        limitType
        percentage
        displayOrder
      }
    }
    levelReferencePyramidTemplate: referencedPyramidsTemplateByCountryAndType(country: $country, referencePyramidType: Level) {
      id
      title
      country
      referencePyramidType
      referencePyramidTemplateDetails {
        id
        range
        limitType
        percentage
        displayOrder
      }
    }
    employeeLevels: getCountryEmployeeLevelsWithOrder(country: $country) {
      displayOrder
      level
    }
    isAgePyramidVisible(country: $country)
  }
`
